/**
 * FoundryAI SSO helper — drop into any React app.
 *
 * Usage in your Login component:
 *   import { ssoEnabled, redirectToHub, FoundrySsoButton } from "@/lib/foundryAiSso";
 *   <FoundrySsoButton />
 *
 * Or imperatively:
 *   if (ssoEnabled()) redirectToHub();
 */
import React from "react";

const PRODUCT_ID = "studio_saketh";

function getEnv(name, fallback = "") {
  // Works in CRA, Next.js, and Vite
  if (typeof process !== "undefined" && process.env) {
    return (
      process.env["REACT_APP_" + name] ||
      process.env["NEXT_PUBLIC_" + name] ||
      fallback
    );
  }
  if (typeof import.meta !== "undefined" && import.meta.env) {
    return import.meta.env["VITE_" + name] || fallback;
  }
  return fallback;
}

export function ssoHubUrl() {
  return getEnv("SSO_HUB_URL", "https://sso.foundryailabs.io").replace(/\/$/, "");
}

export function ssoProductId() {
  return getEnv("SSO_PRODUCT_ID", PRODUCT_ID);
}

export function ssoEnabled() {
  return !!getEnv("SSO_HUB_URL");
}

export function redirectToHub(returnTo) {
  const u = `${ssoHubUrl()}/login?launch=${encodeURIComponent(ssoProductId())}` +
    (returnTo ? `&return_to=${encodeURIComponent(returnTo)}` : "");
  window.location.href = u;
}

export function FoundrySsoButton({ className = "", label = "Sign in with FoundryAI" }) {
  if (!ssoEnabled()) return null;
  return (
    <button
      type="button"
      data-testid="login-foundryai-sso-btn"
      onClick={() => redirectToHub(window.location.pathname)}
      className={
        className ||
        "w-full h-12 flex items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-violet-600 to-cyan-500 hover:opacity-90 text-white font-semibold text-base shadow-lg shadow-violet-600/30 transition-opacity"
      }
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
        <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
      </svg>
      {label}
    </button>
  );
}

/**
 * Hook: auto-redirect anonymous visitors on protected routes.
 *
 * Usage in App.js (or _app.tsx for Next.js):
 *   useAutoForceSso({
 *     isAuthenticated: !!user,
 *     publicRoutes: ["/auth", "/login", "/signup", "/about"]
 *   });
 *
 * Anonymous visitors at any non-public route get bounced to the SSO Hub.
 */
export function useAutoForceSso({ isAuthenticated, publicRoutes = [] }) {
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (!ssoEnabled() || isAuthenticated) return;
    // Saketh apps store their session under `session_token` (not `token`).
    if (localStorage.getItem("session_token") || localStorage.getItem("token")) return;
    if (new URL(window.location.href).searchParams.get("session_id")) return;
    const path = window.location.pathname;
    const isPublic = publicRoutes.some((p) => path === p || path.startsWith(p + "/"));
    if (isPublic) return;
    redirectToHub(path + window.location.search);
  }, [isAuthenticated]);
}
